import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CartProvider } from './contexts/CartContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './admin/AdminLayout';
import UserLayout from './user/UserLayout';
import OrganizationLayout from './organization/Layout';
import InstructorLayout from './instructor/Layout';
import LandingPage from './pages/Landing/LandingPage';
import PrivacyPolicyPage from './pages/Landing/PrivacyPolicyPage';
import TermsOfUsePage from './pages/Landing/TermsOfUsePage';
import FAQPage from './pages/Landing/FAQPage';
import Login from './pages/Auth/Login';
import TwoFactorSetup from './pages/Auth/TwoFactorSetup';
import TwoFactorVerify from './pages/Auth/TwoFactorVerify';

// Lazy-loaded components
const RegisterPage = lazy(() => import('./pages/Landing/RegisterPage'));
const EnquiryPage = lazy(() => import('./pages/Landing/EnquiryPage'));
const CoursesPage = lazy(() => import('./pages/Landing/CoursesPage'));
const CourseDetailPage = lazy(() => import('./pages/Landing/CourseDetailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/Auth/ResetPasswordPage'));

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EduGenieWidget from './components/edugenie/EduGenieWidget';

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <CartProvider>
          <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/2fa/setup" element={<TwoFactorSetup />} />
          <Route path="/2fa/verify" element={<TwoFactorVerify />} />
          <Route path="/register" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><RegisterPage /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><ForgotPasswordPage /></Suspense>} />
          <Route path="/reset-password/:token" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><ResetPasswordPage /></Suspense>} />
          <Route path="/enquiry" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><EnquiryPage /></Suspense>} />
          <Route path="/courses" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><CoursesPage /></Suspense>} />
          <Route path="/courses/:slug" element={<Suspense fallback={<div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>}><CourseDetailPage /></Suspense>} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/faq" element={<FAQPage />} />
          
          
          {/* Protected Admin routes */}
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected User routes */}
          <Route 
            path="/user/*" 
            element={
              <ProtectedRoute requiredRole="user">
                <UserLayout />
              </ProtectedRoute>
            } 
          />

          {/* Protected Organization Admin routes */}
          <Route 
            path="/organization/*" 
            element={
              <ProtectedRoute requiredRole="organization">
                <OrganizationLayout />
              </ProtectedRoute>
            } 
          />

          {/* Protected Instructor routes */}
          <Route
            path="/instructor/*"
            element={
              <ProtectedRoute requiredRole="instructor">
                <InstructorLayout />
              </ProtectedRoute>
            }
          />

          {/* Fallback redirects */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

          <EduGenieWidget />
        
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          </Router>
        </CartProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
